import React, { useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import { Download, Image as ImageIcon } from "@mui/icons-material";
import html2canvas from "html2canvas";
// @ts-ignore
import html2pdf from "html2pdf.js";

// Print-friendly theme
const theme = createTheme({
  typography: {
    fontFamily: "Arial, sans-serif",
  },
});

interface TcListData {
  studentId?: number;
  studentRollno?: string;
  rollNumber?: string;
  studentName: string;
  fatherName?: string;
  motherName?: string;
  className?: string;
  session?: string;
  [key: string]: any; // Allow additional properties
}

interface TcListTemplateProps {
  tcListData: TcListData[];
  schoolName: string;
  district: string;
  block: string;
  diseCode: string;
  mpbseCode: string;
  className: string;
  examType: string;
  sessionYear: string;
  hidePrintButton?: boolean;
}

const TcListTemplate: React.FC<TcListTemplateProps> = ({
  tcListData,
  schoolName,
  district,
  block,
  diseCode,
  mpbseCode,
  className,
  examType,
  sessionYear,
  hidePrintButton = false,
}) => {
  const templateRef = useRef<HTMLDivElement>(null);
  const [isDownloadingImage, setIsDownloadingImage] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  const downloadAsImage = async () => {
    if (!templateRef.current) return;
    setIsDownloadingImage(true);
    try {
      // Wait for content to render
      await new Promise((r) => setTimeout(r, 300));
      
      // Try different html2canvas configurations
      let canvas;
      
      try {
        // Method 1: Basic configuration
        canvas = await html2canvas(templateRef.current, {
          background: "#ffffff",
          logging: true,
        });
      } catch (error) {
        
        // Method 2: With explicit dimensions
        canvas = await html2canvas(templateRef.current, {
          background: "#ffffff",
          logging: true,
          width: templateRef.current.offsetWidth,
          height: templateRef.current.offsetHeight,
          useCORS: true,
          allowTaint: true,
        });
      }
      
      
      if (canvas.width === 0 || canvas.height === 0) {
        throw new Error("Canvas has zero dimensions - content may not be visible");
      }
      
      const a = document.createElement("a");
      a.download = `tc-list-${className}-${examType}-${new Date().toISOString().split("T")[0]}.png`;
      a.href = canvas.toDataURL("image/png", 1.0);
      a.click();
    } catch (error) {
      alert(`Failed to download image: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsDownloadingImage(false);
    }
  };

  const downloadAsPdfFromImage = async () => {
    if (!templateRef.current) return;
    setIsDownloadingPdf(true);
    try {
      await new Promise((r) => setTimeout(r, 80));
      const el = templateRef.current;
      
      // Ensure table container is properly sized for PDF
      const tableContainer = el.querySelector('.MuiTableContainer-root');
      if (tableContainer) {
        (tableContainer as HTMLElement).style.width = '100%';
        (tableContainer as HTMLElement).style.overflow = 'visible';
      }
      
      const opt = {
        margin: [0, 0, 0, 0],
        filename: `tc-list-${className}-${examType}-${new Date().toISOString().split("T")[0]}.pdf`,
        image: { type: "jpeg", quality: 1.0 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff",
          width: el.scrollWidth,
          height: el.scrollHeight,
          scrollX: 0,
          scrollY: 0,
          logging: false,
        },
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "portrait",
          compress: false,
          putOnlyUsedFonts: true,
        },
        pagebreak: { mode: "avoid-all" },
      };
      await html2pdf().set(opt).from(el).save();
    } catch (error) {
      alert("Failed to download PDF.");
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: 2 }}>
        <Paper
          ref={templateRef}
          sx={{
            p: 2,
            maxWidth: "210mm",
            margin: "0 auto",
            backgroundColor: "#ffffff",
            boxShadow: "none",
            width: "100%",
            overflow: "visible",
          }}
        >
          {/* School Header */}
          <Box
            sx={{
              textAlign: "center",
              mb: 2,
              p: 2,
              backgroundColor: "#e8f5e8",
              borderRadius: 1,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                fontSize: "20px",
                mb: 1.5,
                color: "#2e7d32",
                textTransform: "uppercase",
              }}
            >
              {schoolName}
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "start" }}>
              <Box sx={{ textAlign: "left", pl: 2, fontWeight: "bold", width: "50%" }}>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "14px", mb: 0.5, fontWeight: 700 }}
                >
                  DISTRICT- {district.toUpperCase()}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "14px", mb: 0.5, fontWeight: 700 }}
                >
                  BLOCK- {block.toUpperCase()}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "left", pl: 2, fontWeight: "bold", width: "50%" }}>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "14px", mb: 0.5, fontWeight: 700 }}
                >
                  DISE CODE- {diseCode.toUpperCase()}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "14px", fontWeight: 700 }}
                >
                  MPBSE CODE- {mpbseCode.toUpperCase()}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* TC List Title */}
          <Box
            sx={{
              textAlign: "center",
              mb: 2,
              p: 1.5,
              backgroundColor: "#fff3e0",
              borderRadius: 1,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                fontSize: "18px",
                color: "#e65100",
                textTransform: "uppercase",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 2,
              }}
            >
              Transfer Certificate List <Typography sx={{ fontSize: "17px", color: "#525454", fontWeight: "bold" }}>Class:- {className}</Typography>
            </Typography>
          </Box>

          {/* TC List Table */}
          <TableContainer sx={{ width: "100%", overflow: "visible" }}>
            <Table
              sx={{
                border: "1px solid #000",
                width: "100%",
                tableLayout: "fixed",
                "& .MuiTableCell-root": {
                  border: "1px solid #000",
                  padding: "8px",
                  fontSize: "12px",
                  textAlign: "center",
                  fontWeight: "500",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
            >
              <TableHead>
                <TableRow sx={{ backgroundColor: "#d0eaf7" }}>
                  <TableCell sx={{ fontWeight: "bold", width: "8%", fontSize: "13px" }}>
                    S.N.
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "20%", fontSize: "13px" }}>
                    ROLL NUMBER
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "25%", fontSize: "13px" }}>
                    STUDENT'S NAME
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "25%", fontSize: "13px" }}>
                    FATHER'S NAME
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "22%", fontSize: "13px" }}>
                    MOTHER'S NAME
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tcListData.length > 0 ? (
                  tcListData.map((student, index) => (
                    <TableRow
                      key={student.studentId || student.studentRollno || index}
                      sx={{
                        backgroundColor: "#fffef7",
                      }}
                    >
                      <TableCell sx={{ fontWeight: "600" }}>{index + 1}</TableCell>
                      <TableCell sx={{ fontWeight: "600" }}>
                        {student.studentRollno || student.rollNumber || ""}
                      </TableCell>
                      <TableCell sx={{ textAlign: "left", fontWeight: "600", maxWidth: "150px" }}>
                        {student.studentName}
                      </TableCell>
                      <TableCell sx={{ textAlign: "left", fontWeight: "600", maxWidth: "150px" }}>
                        {student.fatherName || ""}
                      </TableCell>
                      <TableCell sx={{ textAlign: "left", fontWeight: "600", maxWidth: "150px" }}>
                        {student.motherName || ""}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  // Show sample data when no real data is available
                  Array.from({ length: 8 }, (_, index) => (
                    <TableRow
                      key={`sample-${index}`}
                      sx={{
                        backgroundColor: "#fffef7",
                      }}
                    >
                      <TableCell sx={{ fontWeight: "600" }}>{index + 1}</TableCell>
                      <TableCell sx={{ fontWeight: "600" }}>
                        10A-B{1001 + index}
                      </TableCell>
                      <TableCell sx={{ textAlign: "left", fontWeight: "600", maxWidth: "150px" }}>
                        Sample Student {index + 1}
                      </TableCell>
                      <TableCell sx={{ textAlign: "left", fontWeight: "600", maxWidth: "150px" }}>
                        Sample Father {index + 1}
                      </TableCell>
                      <TableCell sx={{ textAlign: "left", fontWeight: "600", maxWidth: "150px" }}>
                        Sample Mother {index + 1}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Footer with exam type and session */}
          <Box
            sx={{
              mt: 2,
              textAlign: "center",
              fontSize: "11px",
              color: "#666",
            }}
          >
            <Typography variant="body2">
              Exam Type: {examType} | Session: {sessionYear}
            </Typography>
          </Box>
        </Paper>
        {!hidePrintButton && (
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mb: 2,
              justifyContent: "center",
            }}
          >
            <Button
              variant="contained"
              startIcon={<ImageIcon />}
              onClick={downloadAsImage}
              disabled={isDownloadingImage}
              sx={{ bgcolor: "#1976d2" }}
            >
              {isDownloadingImage ? "Downloading..." : "Download as Image"}
            </Button>
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={downloadAsPdfFromImage}
              disabled={isDownloadingPdf}
              sx={{ bgcolor: "#d32f2f" }}
            >
              {isDownloadingPdf ? "Downloading..." : "Download as PDF"}
            </Button>
          </Box>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default TcListTemplate;
